import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Target,
  ListTodo,
  CalendarDays,
  BarChart3,
  ArrowLeft,
  ArrowRight,
  Plus,
  X,
  Check,
  Clock,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Settings,
  LayoutGrid,
  List,
  Pencil,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { scheduleAllReminders, loadNotificationSettings, syncAppointmentsWithBackend, syncAllFocusWithBackend } from "@/lib/notificationService";
import GoalPlanningView from "@/components/GoalPlanningView";
import HabitTracker from "@/components/habits/HabitTracker";
import { getAllHabits, getLogsForHabit } from "@/lib/habitDB";
import ZielkalenderOnboarding, { KALENDER_ONBOARDING_KEY } from "@/components/ZielkalenderOnboarding";

// ─── Konstanten & Utilities (ausgelagert in utils/) ───────────────────────────
import {
  MONTHS_DE, DAYS_SHORT, HOURS, HOUR_HEIGHT,
  APPOINTMENT_COLORS, COLOR_KEYS, MULTIDAY_COLOR_ORDER,
  DEFAULT_MULTIDAY_COLOR, DEFAULT_SINGLEDAY_COLOR,
  getAppointmentColor, getAppointmentColorByID,
  STORAGE_KEYS, GOOGLE_CLIENT_ID, GOOGLE_SCOPES
} from "@/utils/calendarConstants";

import {
  getLocalDateStr, getWeekNumber, getWeekDates,
  getDaysInMonth, getFirstDayOfMonth,
  loadData, saveData,
  DELETED_HABIT_REMINDERS_KEY,
  getDeletedHabitReminders, addDeletedHabitReminder,
  generateHabitReminderAppointments
} from "@/utils/calendarUtils";
import { runTodoRollover } from "@/utils/todoRollover";

import YearView from "@/components/calendar/YearView";
import AppointmentModal from "@/components/calendar/AppointmentModal";
import MonthView from "@/components/calendar/MonthView";
import WeekView from "@/components/calendar/WeekView";
import EditTodoForm from "@/components/calendar/EditTodoForm";
import TodosView from "@/components/calendar/TodosView";
import useGoogleCalendarSync from "@/hooks/useGoogleCalendarSync";
import { userStorage } from '../utils/userStorage';
// Testphase Modal
function TestPhaseModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
        </div>
        
        <h3 className="text-xl font-bold text-slate-800 text-center mb-3">
          🧪 Testphase
        </h3>
        
        <p className="text-slate-600 text-center mb-4">
          Der <strong>Zielplanungs-Kalender</strong> befindet sich aktuell in der <strong>Testphase</strong>.
        </p>
        
        <p className="text-slate-500 text-sm text-center mb-6">
          Einige Funktionen wie die Google-Kalender-Synchronisierung werden gerade entwickelt und getestet. 
          Bitte melde Fehler oder Verbesserungsvorschläge!
        </p>
        
        <Button 
          onClick={onClose} 
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Check className="w-4 h-4 mr-2" />
          Verstanden, weiter zum Kalender
        </Button>
      </div>
    </div>
  );
}

// Google Calendar Sync Modal
function GoogleSyncModal({ isOpen, onClose, onSync, onDeleteSynced, isLoading, syncStatus, calendars, selectedCalendars, onCalendarToggle, primaryCalendar, onPrimaryCalendarChange }) {
  if (!isOpen) return null;
  
  const isConnected = calendars && calendars.length > 0;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-600" />
            Google Kalender Sync
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {!isConnected ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <p className="text-slate-600 mb-4">
              Verbinde deinen Google Kalender, um Termine zu synchronisieren.
            </p>
            <p className="text-amber-600 text-sm mb-4 bg-amber-50 p-3 rounded-lg">
              ⚠️ <strong>Hinweis:</strong> Diese Funktion ist in der Testphase. 
              Bitte teste auf <strong>burningteam.de</strong>
            </p>
            <Button 
              onClick={onSync} 
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                </svg>
              )}
              Mit Google verbinden
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                <Check className="w-4 h-4" />
                Verbunden mit Google Kalender
              </p>
            </div>
            
            {/* Hauptkalender für Export */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-medium text-blue-800 mb-2 text-sm">📤 Hauptkalender (für neue Termine):</h4>
              <select
                value={primaryCalendar || ''}
                onChange={(e) => onPrimaryCalendarChange(e.target.value)}
                className="w-full p-2 text-sm border border-blue-300 rounded-lg bg-white"
              >
                <option value="">-- Bitte wählen --</option>
                {calendars.filter(c => c.accessRole === 'owner' || c.accessRole === 'writer').map(cal => (
                  <option key={cal.id} value={cal.id}>{cal.summary}</option>
                ))}
              </select>
              <p className="text-blue-600 text-xs mt-1">
                Neue Termine aus der App werden hier gespeichert
              </p>
            </div>
            
            {/* Kalender für Import */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2">📥 Kalender zum Importieren:</h4>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {calendars.map(cal => (
                  <label key={cal.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCalendars.includes(cal.id)}
                      onChange={() => onCalendarToggle(cal.id)}
                      className="w-4 h-4 text-emerald-600 rounded"
                    />
                    <span 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: cal.backgroundColor || '#10b981' }}
                    />
                    <span className="text-sm text-slate-700 truncate">{cal.summary}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <p className="text-slate-500 text-xs">
              📅 Testmodus: Nur diese Woche (Mo-So)
            </p>
            
            <div className="flex gap-2">
              <Button 
                onClick={onSync} 
                disabled={isLoading || selectedCalendars.length === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Jetzt synchronisieren
              </Button>
            </div>
            
            {syncStatus && (
              <p className={`text-sm text-center ${syncStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                {syncStatus.message}
              </p>
            )}
            
            <hr className="border-slate-200" />
            
            <button
              onClick={onDeleteSynced}
              className="w-full text-sm text-red-500 hover:text-red-600 flex items-center justify-center gap-2 py-2"
            >
              <Trash2 className="w-4 h-4" />
              Synchronisierte Termine löschen (nur für Tests)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
// Navigation Buttons - Main navigation (without To-dos, which is now in ViewSelector)
function NavigationButtons({ activeButton, onButtonClick }) {
  const buttons = [
    { id: 'calendar', label: "Kalender", icon: CalendarIcon },
    { id: 'goals',    label: "Ziel",     icon: Target },
    { id: 'tracking', label: "Habits",   icon: BarChart3 },
    { id: 'events',   label: "Events",   icon: CalendarDays }
  ];

  return (
    <div className="flex flex-nowrap gap-1 sm:gap-2 mb-4">
      {buttons.map((btn) => (
        <button
          key={btn.id}
          onClick={() => onButtonClick(btn.id)}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1 px-1.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-sm font-medium transition-all whitespace-nowrap ${
            activeButton === btn.id 
              ? 'bg-emerald-600 text-white shadow-md' 
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <btn.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate">{btn.label}</span>
        </button>
      ))}
    </div>
  );
}

// View Selector - Now includes To-Dos
function ViewSelector({ view, onViewChange }) {
  const views = [
    { id: 'year', label: 'Jahr' },
    { id: 'month', label: 'Monat' },
    { id: 'week', label: 'Woche' },
    { id: 'todos', label: "To-Do's" }
  ];
  
  return (
    <div className="flex items-center gap-1 bg-white rounded-xl p-1 shadow-sm border border-slate-200">
      {views.map((v) => (
        <button
          key={v.id}
          onClick={() => onViewChange(v.id)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            view === v.id ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}
export default function ZielplanungsKalender() {
  const location = useLocation();
  const [view, setView] = useState(() => location.state?.view || 'week');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [habitTrackerHabits, setHabitTrackerHabits] = useState([]);
  const [editingTodo, setEditingTodo] = useState(null); // { todo, sourceType, context }
  const [todoEditRefreshKey, setTodoEditRefreshKey] = useState(0); // Force re-render of views after todo edit
  const [currentDate, setCurrentDate] = useState(() => {
    // Wenn aus Kunden-Mini-Kalender navigiert → Zieldatum setzen
    if (location.state?.targetDate) return new Date(location.state.targetDate);
    return new Date();
  });
  const [activeButton, setActiveButton] = useState('calendar');
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Onboarding: automatisch beim ersten Besuch anzeigen
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(KALENDER_ONBOARDING_KEY));

  // Onboarding auch via burningTeamTour-Trigger öffnen (z.B. von Kunden/Inventar-Tour)
  useEffect(() => {
    try {
      const tour = JSON.parse(localStorage.getItem('burningTeamTour') || '{}');
      if (tour?.shouldStart && tour?.page === 'zielplanung') {
        localStorage.removeItem('burningTeamTour');
        setShowOnboarding(true);
      }
    } catch {}
  }, []);

  // Todo-Rollover (Tag/Woche/Monat/Jahr) – läuft auf Hauptkomponenten-Ebene,
  // damit der Rollover auch passiert, wenn der User nie auf den Todos-Tab klickt.
  // Periodischer Check alle 60s + visibilitychange/focus deckt PWA-Hintergrund-
  // Szenarien ab (z.B. App über Mitternacht offen).
  const lastRolloverDayRef = useRef(null);
  useEffect(() => {
    const run = () => {
      const today = getLocalDateStr(new Date());
      if (lastRolloverDayRef.current === today) return;
      runTodoRollover(new Date());
      lastRolloverDayRef.current = today;
      // Notify Listener (TodosView, etc.) dass sich Storage geändert hat
      window.dispatchEvent(new CustomEvent('burningteam-todo-rollover'));
    };
    run();
    const intervalId = setInterval(run, 60 * 1000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, []);
  
  // Todo-to-Calendar transfer state
  const [todoCalendarData, setTodoCalendarData] = useState(null);
  
  const [yearGoals, setYearGoals] = useState(() => loadData(STORAGE_KEYS.yearGoals));
  const [yearPlanning, setYearPlanning] = useState(() => loadData(STORAGE_KEYS.yearPlanning, {}));
  const [monthGoals, setMonthGoals] = useState(() => loadData(STORAGE_KEYS.monthGoals));
  const [monthAppointments, setMonthAppointments] = useState(() => loadData('zielplanung_month_appointments', {}));
  const [weekGoals, setWeekGoals] = useState(() => loadData(STORAGE_KEYS.weekGoals));
  const [tasks, setTasks] = useState(() => loadData(STORAGE_KEYS.tasks));
  const [dayFocus, setDayFocus] = useState(() => loadData(STORAGE_KEYS.dayFocus));
  const [appointments, setAppointments] = useState(() => loadData(STORAGE_KEYS.appointments, []));
  const [goalProgress, setGoalProgress] = useState(0);
  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  
  // Open Todos (Dieses Jahr / Jahres-Aufgaben)
  const [openTodos, setOpenTodos] = useState(() => loadData(STORAGE_KEYS.backlogTodos, []));
  
  // Test Phase Modal
  const [showTestModal, setShowTestModal] = useState(() => {
    return !loadData(STORAGE_KEYS.testModalShown, false);
  });
  

  // ─── Google Calendar Sync (ausgelagert in useGoogleCalendarSync) ───────────
  const {
    showSyncModal, setShowSyncModal,
    googleCalendars, selectedCalendars, primaryCalendar,
    syncLoading, syncStatus, syncedAppointments,
    handleSyncClick, handleCalendarToggle,
    handlePrimaryCalendarChange, deleteSyncedAppointments,
    syncGoogleEvents
  } = useGoogleCalendarSync({ appointments, setAppointments });

  const handleTestModalClose = () => {
    setShowTestModal(false);
    saveData(STORAGE_KEYS.testModalShown, true);
  };

  const handleYearGoalChange = (year, goal) => {
    const updated = { ...yearGoals, [year]: goal };
    setYearGoals(updated);
    saveData(STORAGE_KEYS.yearGoals, updated);
  };

  const handleYearPlanningChange = (monthKey, items) => {
    const updated = { ...yearPlanning, [monthKey]: items };
    setYearPlanning(updated);
    saveData(STORAGE_KEYS.yearPlanning, updated);
  };

  const handleMonthGoalChange = (monthKey, goal) => {
    const updated = { ...monthGoals, [monthKey]: goal };
    setMonthGoals(updated);
    saveData(STORAGE_KEYS.monthGoals, updated);
  };

  const handleMonthAppointmentsChange = (monthKey, appointmentList) => {
    // Find actually NEW appointments
    const existingIds = new Set((monthAppointments?.[monthKey] || []).map(a => a.id));
    const newAppointments = appointmentList.filter(apt => !existingIds.has(apt.id));
    
    const updated = { ...monthAppointments, [monthKey]: appointmentList };
    setMonthAppointments(updated);
    saveData('zielplanung_month_appointments', updated);
    
    // Only sync NEW items to yearPlanning
    if (newAppointments.length > 0) {
      setYearPlanning(prev => {
        const currentYearItems = prev?.[monthKey] || [];
        let newYearItems = [...currentYearItems];
        let changed = false;
        
        newAppointments.forEach(apt => {
          if (apt.from && !currentYearItems.find(y => y.id === apt.id)) {
            newYearItems.push({
              id: apt.id,
              title: apt.title,
              note: apt.note || '',
              from: apt.from,
              to: apt.to || apt.from,
              priority: apt.priority || 5,
              syncedFrom: 'month'
            });
            changed = true;
          }
        });
        
        if (changed) {
          const updatedYear = { ...prev, [monthKey]: newYearItems };
          saveData(STORAGE_KEYS.yearPlanning, updatedYear);
          return updatedYear;
        }
        return prev;
      });
    }
  };

  const handleWeekGoalChange = (weekKey, goal) => {
    const updated = { ...weekGoals, [weekKey]: goal };
    setWeekGoals(updated);
    saveData(STORAGE_KEYS.weekGoals, updated);
  };

  const handleTaskChange = (monthKey, taskList) => {
    const updated = { ...tasks, [monthKey]: taskList };
    setTasks(updated);
    saveData(STORAGE_KEYS.tasks, updated);
  };

  const handleDayFocusChange = (dateKey, focusText) => {
    const updated = { ...dayFocus, [dateKey]: focusText };
    setDayFocus(updated);
    saveData(STORAGE_KEYS.dayFocus, updated);
    
    // Sync only if today or tomorrow changed
    const now = new Date();
    const today = getLocalDateStr(now);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = getLocalDateStr(tomorrow);
    
    if (dateKey === today || dateKey === tomorrowStr) {
      syncAllFocusWithBackend(updated);
    }
  };

  const API = process.env.REACT_APP_BACKEND_URL;

  // Zentrale Funktion: Erledigt-Status in ALLEN Ansichten synchronisieren (Woche, Monat, Jahr + Kontakt-API)
  const handleToggleDoneByTaskId = async (taskId, newDone, contactRef) => {
    if (!taskId) return;

    // Woche aktualisieren
    const updatedApts = (appointments || []).map(a =>
      a.contactRef?.taskId === taskId ? { ...a, done: newDone } : a
    );
    setAppointments(updatedApts);
    saveData(STORAGE_KEYS.appointments, updatedApts);

    // Monat aktualisieren
    const updatedMonthData = {};
    Object.keys(monthAppointments || {}).forEach(key => {
      updatedMonthData[key] = (monthAppointments[key] || []).map(a =>
        a.contactRef?.taskId === taskId ? { ...a, done: newDone } : a
      );
    });
    setMonthAppointments(updatedMonthData);
    saveData('zielplanung_month_appointments', updatedMonthData);

    // Jahr aktualisieren
    const updatedYearData = {};
    Object.keys(yearPlanning || {}).forEach(key => {
      updatedYearData[key] = (yearPlanning[key] || []).map(a =>
        a.contactRef?.taskId === taskId ? { ...a, done: newDone } : a
      );
    });
    setYearPlanning(updatedYearData);
    saveData(STORAGE_KEYS.yearPlanning, updatedYearData);

    // Kontakt-API synchronisieren
    if (contactRef?.id && contactRef?.taskId) {
      try {
        const endpoint = contactRef.typ === 'ich'
          ? `${API}/api/auth/me/tasks/${contactRef.taskId}`
          : `${API}/api/teampartner/${contactRef.id}/tasks/${contactRef.taskId}`;
        await axios.patch(endpoint, { done: newDone });
      } catch { /* Sync-Fehler ignorieren */ }
    }
  };

  const handleToggleAppointmentDone = async (appointmentId) => {
    const apt = (appointments || []).find(a => a.id === appointmentId);
    if (!apt) return;
    const newDone = !apt.done;

    if (apt.contactRef?.taskId) {
      await handleToggleDoneByTaskId(apt.contactRef.taskId, newDone, apt.contactRef);
    } else {
      // Einfacher Toggle ohne Kontakt-Verknüpfung
      const updated = (appointments || []).map(a =>
        a.id === appointmentId ? { ...a, done: newDone } : a
      );
      setAppointments(updated);
      saveData(STORAGE_KEYS.appointments, updated);
    }
  };

  const handleAppointmentChange = (appointmentList) => {
    setAppointments(appointmentList);
    saveData(STORAGE_KEYS.appointments, appointmentList);
    
    // Only sync NEW week appointments to YEAR view (NOT month - month already shows appointments directly)
    // SKIP appointments that came from transfer - they already exist in month/year view
    // SKIP habit reminder entries - they are auto-generated, not user-created planning items
    const existingIds = new Set((appointments || []).map(a => a.id));
    const newAppointments = appointmentList.filter(apt => !existingIds.has(apt.id) && !apt.transferredFrom && !apt.isHabitReminder);
    
    newAppointments.forEach(apt => {
      if (apt.date) {
        const [year, month] = apt.date.split('-');
        const monthKey = `${year}-${month}`;
        
        // Only sync to yearPlanning (NOT monthAppointments - would create duplicates)
        setYearPlanning(prev => {
          const currentYearItems = prev?.[monthKey] || [];
          if (!currentYearItems.find(y => y.id === apt.id)) {
            const newYearItem = {
              id: apt.id,
              title: apt.title,
              from: apt.date,
              to: apt.isMultiDay && apt.endDate ? apt.endDate : apt.date,
              note: `${apt.startTime}-${apt.endTime}`,
              priority: 5,
              syncedFrom: 'week',
              isMultiDay: apt.isMultiDay || false,
              startTime: apt.startTime,
              endTime: apt.endTime,
              transferredToWeek: true  // Mark as transferred since it's from week
            };
            const updated = { ...prev, [monthKey]: [...currentYearItems, newYearItem] };
            saveData(STORAGE_KEYS.yearPlanning, updated);
            
            // Also add to transferred IDs so it shows the checkmark
            try {
              const transferredIds = JSON.parse(userStorage.getItem('zielplanung_transferred_ids') || '[]');
              if (!transferredIds.includes(apt.id)) {
                transferredIds.push(apt.id);
                userStorage.setItem('zielplanung_transferred_ids', JSON.stringify(transferredIds));
              }
            } catch (e) {}
            
            return updated;
          }
          return prev;
        });
      }
    });
  };

  // Sync habit reminder appointments when habits change
  const handleHabitsChanged = useCallback((habits, allLogs = {}) => {
    setHabitTrackerHabits(habits);

    // Update week/month/year view appointments (individual day entries only)
    setAppointments(prev => {
      const regular = (prev || []).filter(a => !a.isHabitReminder);
      const habitEntries = generateHabitReminderAppointments(habits, allLogs);
      const merged = [...regular, ...habitEntries];
      saveData(STORAGE_KEYS.appointments, merged);
      return merged;
    });

    // Clean up any leftover habit reminder spans from yearPlanning (legacy)
    setYearPlanning(prev => {
      let changed = false;
      const updated = {};
      for (const [key, items] of Object.entries(prev || {})) {
        const filtered = (items || []).filter(item => !item.isHabitReminderSpan);
        if (filtered.length !== (items || []).length) changed = true;
        updated[key] = filtered;
      }
      if (changed) saveData(STORAGE_KEYS.yearPlanning, updated);
      return changed ? updated : prev;
    });
  }, []);

  // Load habit data on mount so calendar views show reminders even before visiting Tracking tab
  useEffect(() => {
    const loadHabitData = async () => {
      try {
        const allHabits = await getAllHabits();
        const logsMap = {};
        for (const habit of allHabits) {
          logsMap[habit.id] = await getLogsForHabit(habit.id);
        }
        handleHabitsChanged(allHabits, logsMap);
      } catch (e) { /* IndexedDB may not be ready yet */ }
    };
    loadHabitData();
  }, [handleHabitsChanged]);

  // Open todo edit modal (called from any view)
  const openTodoEditModal = (todo, sourceType, context = {}) => {
    setEditingTodo({ todo, sourceType, context });
  };

  // Save edited todo back to the correct storage
  const handleSaveTodoEdit = (updatedData) => {
    if (!editingTodo) return;
    const { todo, sourceType, context } = editingTodo;
    const updated = { ...todo, ...updatedData };

    if (sourceType === 'monthly') {
      const monthKey = context.monthKey;
      const stored = JSON.parse(userStorage.getItem('zielplanung_monthly_todos') || '{}');
      stored[monthKey] = (stored[monthKey] || []).map(t => t.id === todo.id ? updated : t);
      userStorage.setItem('zielplanung_monthly_todos', JSON.stringify(stored));
    } else if (sourceType === 'weekly') {
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      for (const key of Object.keys(stored)) {
        stored[key] = (stored[key] || []).map(t => t.id === todo.id ? updated : t);
      }
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    } else if (sourceType === 'daily') {
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      for (const key of Object.keys(stored)) {
        stored[key] = (stored[key] || []).map(t => t.id === todo.id ? updated : t);
      }
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else if (sourceType === 'open') {
      setOpenTodos(prev => prev.map(t => t.id === todo.id ? updated : t));
    }
    setTodoEditRefreshKey(k => k + 1); // Force re-render of all views
    setEditingTodo(null);
  };
  
  // Handle transfer from Todo to Calendar
  const handleTransferToCalendar = (todoData) => {
    // Set the data and switch to the appropriate view
    setTodoCalendarData(todoData);
    setActiveButton('calendar');
    
    if (todoData.type === 'week') {
      setView('week');
    } else if (todoData.type === 'month') {
      setView('month');
    } else if (todoData.type === 'year') {
      setView('year');
    }
  };

  // On mount: check for pending transfer from ZuErledigienModal (Kontakt → Kalender via Navigation)
  // This is kept as a fallback but the primary flow is now inline modals (no navigation needed)
  useEffect(() => {
    const pending = userStorage.getItem('burningteam_pending_calendar_transfer');
    if (pending) {
      userStorage.removeItem('burningteam_pending_calendar_transfer');
      try {
        const data = JSON.parse(pending);
        handleTransferToCalendar(data);
      } catch (e) { /* ignore parse errors */ }
    }
  }, []); // eslint-disable-line

  // Listener: Kalender-Termine neu laden wenn Habit-Konflikt-Modal Änderungen schreibt
  useEffect(() => {
    const handleCalendarSync = () => {
      setAppointments(loadData(STORAGE_KEYS.appointments, []));
    };
    window.addEventListener('burningteam-calendar-sync', handleCalendarSync);
    return () => window.removeEventListener('burningteam-calendar-sync', handleCalendarSync);
  }, []);
  
  // Central delete function - removes from ALL views (bidirectional cascade)
  // Deleting from any view will delete related items from all other views
  const handleDeleteFromAllViews = (itemId, dateOrMonthKey, sourceView = null) => {
    // If this is a habit reminder entry, track deletion + remove from appointments only
    if (typeof itemId === 'string' && itemId.startsWith('hr_')) {
      addDeletedHabitReminder(itemId);
      setAppointments(prev => {
        const updated = (prev || []).filter(a => a.id !== itemId);
        saveData(STORAGE_KEYS.appointments, updated);
        return updated;
      });
      return;
    }

    // Handle 'year-' prefix - extract numeric ID
    let cleanItemId = itemId;
    if (typeof itemId === 'string' && itemId.startsWith('year-')) {
      cleanItemId = parseInt(itemId.replace('year-', ''));
    }
    
    // Determine the monthKey
    let monthKey = dateOrMonthKey;
    if (dateOrMonthKey && dateOrMonthKey.length > 7) {
      // It's a full date like "2026-01-15", extract month key
      monthKey = dateOrMonthKey.substring(0, 7);
    }
    
    // Collect all IDs that need to be deleted from transferred IDs list
    const idsToRemoveFromTransferred = new Set();
    idsToRemoveFromTransferred.add(itemId);
    idsToRemoveFromTransferred.add(cleanItemId);
    idsToRemoveFromTransferred.add(`month-${itemId}`);
    idsToRemoveFromTransferred.add(`month-${cleanItemId}`);
    idsToRemoveFromTransferred.add(`year-${itemId}`);
    idsToRemoveFromTransferred.add(`year-${cleanItemId}`);
    
    // Find the item being deleted from any source to get matching info
    let itemToDelete = null;
    let linkedYearId = null;
    let linkedMonthId = null;
    let linkedWeekId = null;
    
    // Check week appointments first
    const weekApt = appointments.find(apt => apt.id === itemId || apt.id === cleanItemId);
    if (weekApt) {
      itemToDelete = weekApt;
      linkedMonthId = weekApt.originalMonthId;
      linkedYearId = weekApt.originalYearId;
    }
    
    // Check month appointments
    if (!itemToDelete && monthKey) {
      const monthApts = monthAppointments?.[monthKey] || [];
      const monthApt = monthApts.find(apt => apt.id === itemId || apt.id === cleanItemId);
      if (monthApt) {
        itemToDelete = monthApt;
        linkedYearId = monthApt.originalYearId;
      }
    }
    
    // Check year planning - IMPORTANT for year-transferred items
    if (monthKey) {
      // Read from localStorage for most up-to-date data
      let yearItems = yearPlanning?.[monthKey] || [];
      try {
        const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
        yearItems = stored?.[monthKey] || yearItems;
      } catch (e) {}
      
      const yearItem = yearItems.find(item => item.id === itemId || item.id === cleanItemId);
      if (yearItem) {
        if (!itemToDelete) {
          itemToDelete = yearItem;
        }
        // Use the year item's ID as linkedYearId for deletion
        linkedYearId = yearItem.id;
      }
    }

    // Bidirektionale Sync: Verknüpfte Kontakt-Aufgabe löschen (fire-and-forget)
    if (itemToDelete?.contactRef?.id && itemToDelete?.contactRef?.taskId) {
      const ref = itemToDelete.contactRef;
      (async () => {
        try {
          if (ref.typ === 'ich') {
            await axios.delete(`${API}/api/auth/me/tasks/${ref.taskId}`);
          } else {
            await axios.delete(`${API}/api/teampartner/${ref.id}/tasks/${ref.taskId}`);
          }
        } catch { /* Sync-Fehler ignorieren */ }
      })();
    }
    
    // Get title and date for fallback matching
    const title = itemToDelete?.title || itemToDelete?.note;
    const dateForMatch = itemToDelete?.date || itemToDelete?.from;
    
    // --- DELETE FROM WEEK VIEW ---
    // taskIdToDelete: used to cascade-delete linked entries across all views (from ZuErledigienModal)
    const taskIdToDelete = itemToDelete?.contactRef?.taskId;
    // Match by: direct ID, linkedWeekId, originalMonthId, originalYearId, title+date, OR linked taskId
    const updatedAppointments = appointments.filter(apt => {
      if (apt.id === itemId || apt.id === cleanItemId) return false;
      if (linkedWeekId && apt.id === linkedWeekId) return false;
      if (apt.originalMonthId === itemId || apt.originalMonthId === cleanItemId) return false;
      if (apt.originalYearId === itemId || apt.originalYearId === cleanItemId) return false;
      if (linkedMonthId && apt.originalMonthId === linkedMonthId) return false;
      if (linkedYearId && (apt.originalYearId === linkedYearId || apt.id === linkedYearId)) return false;
      if (taskIdToDelete && apt.contactRef?.taskId === taskIdToDelete) return false;
      // Fallback: match by title and date
      if (title && dateForMatch && apt.title === title && apt.date === dateForMatch) return false;
      return true;
    });
    
    // Collect IDs being deleted for transferred list cleanup
    appointments.forEach(apt => {
      if (!updatedAppointments.find(a => a.id === apt.id)) {
        idsToRemoveFromTransferred.add(apt.id);
        idsToRemoveFromTransferred.add(`month-${apt.id}`);
        idsToRemoveFromTransferred.add(`year-${apt.id}`);
        if (apt.originalMonthId) idsToRemoveFromTransferred.add(apt.originalMonthId);
        if (apt.originalYearId) idsToRemoveFromTransferred.add(apt.originalYearId);
      }
    });
    
    if (updatedAppointments.length !== appointments.length) {
      setAppointments(updatedAppointments);
      saveData(STORAGE_KEYS.appointments, updatedAppointments);
    }
    
    // --- DELETE FROM MONTH VIEW ---
    if (monthKey) {
      const currentMonthApts = monthAppointments?.[monthKey] || [];
      const updatedMonthApts = currentMonthApts.filter(apt => {
        if (apt.id === itemId || apt.id === cleanItemId) return false;
        if (linkedMonthId && apt.id === linkedMonthId) return false;
        if (apt.originalYearId === itemId || apt.originalYearId === cleanItemId) return false;
        if (linkedYearId && apt.originalYearId === linkedYearId) return false;
        if (taskIdToDelete && apt.contactRef?.taskId === taskIdToDelete) return false;
        // Fallback: match by title and date
        if (title && dateForMatch && apt.title === title && apt.from === dateForMatch) return false;
        return true;
      });
      
      // Collect IDs being deleted
      currentMonthApts.forEach(apt => {
        if (!updatedMonthApts.find(a => a.id === apt.id)) {
          idsToRemoveFromTransferred.add(apt.id);
          idsToRemoveFromTransferred.add(`month-${apt.id}`);
          idsToRemoveFromTransferred.add(`year-${apt.id}`);
          if (apt.originalYearId) idsToRemoveFromTransferred.add(apt.originalYearId);
        }
      });
      
      if (updatedMonthApts.length !== currentMonthApts.length) {
        const newMonthAppointments = { ...monthAppointments, [monthKey]: updatedMonthApts };
        setMonthAppointments(newMonthAppointments);
        saveData('zielplanung_month_appointments', newMonthAppointments);
      }
    }
    
    // --- DELETE FROM YEAR VIEW ---
    if (monthKey) {
      // Read from localStorage for most up-to-date data
      let currentYearItems = yearPlanning?.[monthKey] || [];
      try {
        const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
        currentYearItems = stored?.[monthKey] || currentYearItems;
      } catch (e) {}
      
      const updatedYearItems = currentYearItems.filter(item => {
        if (item.id === itemId || item.id === cleanItemId) return false;
        if (linkedYearId && item.id === linkedYearId) return false;
        // Fallback: match by title and date
        if (title && dateForMatch && item.title === title && item.from === dateForMatch) return false;
        return true;
      });
      
      // Collect IDs being deleted
      currentYearItems.forEach(item => {
        if (!updatedYearItems.find(i => i.id === item.id)) {
          idsToRemoveFromTransferred.add(item.id);
          idsToRemoveFromTransferred.add(`month-${item.id}`);
          idsToRemoveFromTransferred.add(`year-${item.id}`);
        }
      });
      
      if (updatedYearItems.length !== currentYearItems.length) {
        const newYearPlanning = { ...yearPlanning, [monthKey]: updatedYearItems };
        setYearPlanning(newYearPlanning);
        saveData(STORAGE_KEYS.yearPlanning, newYearPlanning);
        // Also save directly to localStorage
        try {
          const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
          stored[monthKey] = updatedYearItems;
          userStorage.setItem('zielplanung_year_planning', JSON.stringify(stored));
        } catch (e) {}
      }
    }
    
    // Global cascade: delete ALL entries with same taskId across ALL months (contact-linked items)
    if (taskIdToDelete) {
      const allMonthStored = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
      let monthChanged = false;
      Object.keys(allMonthStored).forEach(key => {
        const orig = allMonthStored[key] || [];
        allMonthStored[key] = orig.filter(a => a.contactRef?.taskId !== taskIdToDelete);
        if (allMonthStored[key].length !== orig.length) monthChanged = true;
      });
      if (monthChanged) {
        userStorage.setItem('zielplanung_month_appointments', JSON.stringify(allMonthStored));
        setMonthAppointments(allMonthStored);
      }
      const allYearStored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      let yearChanged = false;
      Object.keys(allYearStored).forEach(key => {
        const orig = allYearStored[key] || [];
        allYearStored[key] = orig.filter(i => i.contactRef?.taskId !== taskIdToDelete);
        if (allYearStored[key].length !== orig.length) yearChanged = true;
      });
      if (yearChanged) {
        userStorage.setItem('zielplanung_year_planning', JSON.stringify(allYearStored));
        setYearPlanning(allYearStored);
      }
      // Also clean week appointments globally
      const allWeekStored = JSON.parse(userStorage.getItem(STORAGE_KEYS.appointments) || '[]');
      const filteredWeek = allWeekStored.filter(a => a.contactRef?.taskId !== taskIdToDelete);
      if (filteredWeek.length !== allWeekStored.length) {
        userStorage.setItem(STORAGE_KEYS.appointments, JSON.stringify(filteredWeek));
        setAppointments(filteredWeek);
      }
    }

    // Clean up transferred IDs list
    try {
      let transferredIds = JSON.parse(userStorage.getItem('zielplanung_transferred_ids') || '[]');
      transferredIds = transferredIds.filter(id => !idsToRemoveFromTransferred.has(id) && !idsToRemoveFromTransferred.has(String(id)));
      userStorage.setItem('zielplanung_transferred_ids', JSON.stringify(transferredIds));
    } catch (e) {}
  };

  // Refs für Navigationsleisten-Swipe (kein State → kein Re-Render während Drag)
  const navSwipeStartX = useRef(null);
  const navSwipeDeltaX = useRef(0);

  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (view === 'year') newDate.setFullYear(newDate.getFullYear() - 1);
    else if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'year') newDate.setFullYear(newDate.getFullYear() + 1);
    else if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const handleMonthClick = (monthIndex) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(monthIndex);
    setCurrentDate(newDate);
    setView('month');
  };

  const handleDayClick = (day) => {
    const newDate = new Date(currentDate);
    newDate.setDate(day);
    setCurrentDate(newDate);
    setView('week');
  };

  const getTitle = () => {
    if (view === 'year') return currentDate.getFullYear().toString();
    if (view === 'month') return `${MONTHS_DE[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    return `KW ${getWeekNumber(currentDate)} / ${currentDate.getFullYear()}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30">
      {/* Test Phase Modal */}
      <TestPhaseModal isOpen={showTestModal} onClose={handleTestModalClose} />
      
      {/* Google Sync Modal */}
      <GoogleSyncModal 
        isOpen={showSyncModal} 
        onClose={() => setShowSyncModal(false)}
        onSync={syncGoogleEvents}
        onDeleteSynced={deleteSyncedAppointments}
        isLoading={syncLoading}
        syncStatus={syncStatus}
        calendars={googleCalendars}
        selectedCalendars={selectedCalendars}
        onCalendarToggle={handleCalendarToggle}
        primaryCalendar={primaryCalendar}
        onPrimaryCalendarChange={handlePrimaryCalendarChange}
      />
      
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-2">
              <Link to="/main-hub" className="flex items-center text-slate-600 hover:text-emerald-600">
                <ArrowLeft className="w-5 h-5 mr-1" />
                <span className="font-medium hidden sm:inline">Zurück</span>
              </Link>
              <Link to="/kunden" title="Zu Kunden">
                <Button variant="outline" size="icon" className="border-emerald-300 hover:bg-emerald-50 text-emerald-700 h-8 w-8">
                  <Users className="w-4 h-4" />
                </Button>
              </Link>
              {/* Info / Onboarding Button – "i" wie auf der Kunden-Seite */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowOnboarding(true)}
                className="border-emerald-300 hover:bg-emerald-50 h-8 w-8"
                title="Funktionen erklärt"
              >
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Button>
            </div>
            
            {/* Titel: absolut zentriert damit er auf Mobile immer mittig bleibt */}
            <h1 className="absolute left-1/2 -translate-x-1/2 text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2 pointer-events-none select-none">
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              <span className="hidden sm:inline">Zielplanungs-Kalender</span>
              <span className="sm:hidden">Kalender</span>
            </h1>
            
            {/* Sync Buttons */}
            <div className="flex items-center gap-1">
              {/* Sync Button - syncs immediately if configured */}
              <button 
                onClick={handleSyncClick}
                disabled={syncLoading}
                className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {syncLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Sync</span>
              </button>
              
              {/* Settings Button - opens modal */}
              <button 
                onClick={() => setShowSyncModal(true)}
                className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                title="Sync-Einstellungen"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Progress Bar - Always visible */}
        <div className="bg-white rounded-xl p-2 mb-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-1 gap-1">
            <span className="text-xs font-medium text-slate-600">Fortschritt EXP</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMilestoneModalOpen(true)}
                className="text-xs font-semibold px-1.5 py-1 rounded-md border border-emerald-400 text-emerald-700 hover:bg-emerald-50 flex items-center gap-0.5"
              >
                <Check className="w-3 h-3" />
                Meilenstein Check
              </button>
              <span className="text-sm font-bold text-emerald-600">{goalProgress}%</span>
            </div>
          </div>
          <div className="bg-slate-100 rounded-full h-2.5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-500 ease-out"
              style={{ width: `${goalProgress}%` }}
            />
          </div>
        </div>

        <NavigationButtons activeButton={activeButton} onButtonClick={setActiveButton} />

        {/* Ziel Planung View - always rendered for milestone modal access */}
        <div className={activeButton === 'goals' ? '' : 'hidden'}>
          <GoalPlanningView 
            onProgressChange={setGoalProgress} 
            milestoneModalOpen={milestoneModalOpen}
            onMilestoneModalClose={() => setMilestoneModalOpen(false)}
            onNavigateTo={setActiveButton}
          />
        </div>

        {/* Kalender View - includes Year, Month, Week, and To-Dos */}
        {activeButton === 'calendar' && (
          <>
            {/* Fixed Tab Bar - ViewSelector always on top */}
            <div className="sticky top-[57px] z-30 bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 pb-2 -mx-4 px-4 pt-2">
              <div className="flex justify-center mb-3">
                <ViewSelector view={view} onViewChange={setView} />
              </div>
              
              {/* Date Navigation - always below tabs, hidden for To-Dos */}
              <div
                className={`flex items-center justify-center gap-1 sm:gap-2 ${view === 'todos' ? 'invisible h-0 overflow-hidden' : ''}`}
                onTouchStart={(e) => {
                  navSwipeStartX.current = e.targetTouches[0].clientX;
                  navSwipeDeltaX.current = 0;
                }}
                onTouchMove={(e) => {
                  if (navSwipeStartX.current === null) return;
                  const dx = e.targetTouches[0].clientX - navSwipeStartX.current;
                  navSwipeDeltaX.current = dx;
                  if (Math.abs(dx) > 12) e.preventDefault();
                }}
                onTouchEnd={() => {
                  const dx = navSwipeDeltaX.current;
                  navSwipeStartX.current = null;
                  navSwipeDeltaX.current = 0;
                  if (Math.abs(dx) < 50) return;
                  if (dx < 0) navigateNext();
                  else navigatePrev();
                }}
                style={{ touchAction: 'pan-y' }}
              >
                {/* View Mode Toggle - Calendar/List */}
                <div className="flex bg-white rounded-lg border border-slate-200 shadow-sm p-0.5">
                  <button 
                    onClick={() => setViewMode('calendar')}
                    className={`p-1.5 sm:p-2 rounded-md transition-colors ${viewMode === 'calendar' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                    title="Kalenderansicht"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 sm:p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                    title="Listenansicht"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                <button onClick={navigatePrev} className="p-1.5 sm:p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 shadow-sm">
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                </button>
                
                <button 
                  onClick={() => setShowDatePicker(true)}
                  className="px-2 py-1.5 bg-white rounded-lg border border-slate-200 min-w-[120px] sm:min-w-[160px] text-center shadow-sm hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <span className="font-semibold text-slate-800 text-xs sm:text-sm">{getTitle()}</span>
                </button>
                
                <button onClick={navigateNext} className="p-1.5 sm:p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 shadow-sm">
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                </button>
                
                <button onClick={goToToday} className="px-2 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 shadow-sm">
                  Heute
                </button>
              </div>
              
              {/* Date Picker Modal */}
              {showDatePicker && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDatePicker(false)}>
                  <div className="bg-white rounded-xl p-4 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 text-center">
                      {view === 'year' && 'Jahr auswählen'}
                      {view === 'month' && 'Monat auswählen'}
                      {view === 'week' && 'Woche auswählen'}
                    </h3>
                    
                    {/* Year Picker */}
                    {view === 'year' && (
                      <div className="grid grid-cols-3 gap-2">
                        {Array.from({ length: 9 }, (_, i) => currentDate.getFullYear() - 4 + i).map(year => (
                          <button
                            key={year}
                            onClick={() => { setCurrentDate(new Date(year, 0, 1)); setShowDatePicker(false); }}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                              year === currentDate.getFullYear() 
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Month Picker */}
                    {view === 'month' && (
                      <div className="space-y-4">
                        {/* Year selector */}
                        <div className="flex items-center justify-center gap-2">
                          <button 
                            onClick={() => setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1))}
                            className="p-1 rounded hover:bg-slate-100"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <span className="font-semibold text-lg min-w-[60px] text-center">{currentDate.getFullYear()}</span>
                          <button 
                            onClick={() => setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1))}
                            className="p-1 rounded hover:bg-slate-100"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                        {/* Month grid */}
                        <div className="grid grid-cols-3 gap-2">
                          {['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'].map((month, idx) => (
                            <button
                              key={month}
                              onClick={() => { setCurrentDate(new Date(currentDate.getFullYear(), idx, 1)); setShowDatePicker(false); }}
                              className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                idx === currentDate.getMonth() 
                                  ? 'bg-emerald-500 text-white' 
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                              }`}
                            >
                              {month}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Week Picker */}
                    {view === 'week' && (
                      <div className="space-y-4">
                        {/* Year and Month selector */}
                        <div className="flex items-center justify-center gap-2">
                          <button 
                            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate()))}
                            className="p-1 rounded hover:bg-slate-100"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <span className="font-semibold text-sm min-w-[120px] text-center">
                            {['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][currentDate.getMonth()]} {currentDate.getFullYear()}
                          </span>
                          <button 
                            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()))}
                            className="p-1 rounded hover:bg-slate-100"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                        {/* Week grid - show weeks 1-52 */}
                        <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
                          {Array.from({ length: 52 }, (_, i) => i + 1).map(week => {
                            // Calculate date for this week
                            const jan4 = new Date(currentDate.getFullYear(), 0, 4);
                            const dayOfWeek = jan4.getDay() || 7;
                            const weekStart = new Date(jan4);
                            weekStart.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
                            
                            // Get current week number
                            const currDate = new Date(currentDate);
                            const startOfYear = new Date(currDate.getFullYear(), 0, 1);
                            const days = Math.floor((currDate - startOfYear) / (24 * 60 * 60 * 1000));
                            const currentWeek = Math.ceil((days + startOfYear.getDay() + 1) / 7);
                            
                            return (
                              <button
                                key={week}
                                onClick={() => { setCurrentDate(weekStart); setShowDatePicker(false); }}
                                className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                                  week === currentWeek 
                                    ? 'bg-emerald-500 text-white' 
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                }`}
                              >
                                KW {week}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => setShowDatePicker(false)}
                      className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </div>

            {view === 'year' && (
              <YearView year={currentDate.getFullYear()} onMonthClick={handleMonthClick} yearPlanning={yearPlanning} onYearPlanningChange={handleYearPlanningChange} monthAppointments={monthAppointments} onMonthAppointmentsChange={handleMonthAppointmentsChange} onDeleteFromAll={handleDeleteFromAllViews} viewMode={viewMode} todoCalendarData={todoCalendarData} onTodoCalendarHandled={() => setTodoCalendarData(null)} yearGoals={yearGoals} onYearGoalChange={handleYearGoalChange} openTodos={openTodos} appointments={appointments} onEditTodo={openTodoEditModal} todoEditRefreshKey={todoEditRefreshKey} onToggleDoneByTaskId={handleToggleDoneByTaskId} />
            )}
            
            {view === 'month' && (
              <MonthView year={currentDate.getFullYear()} month={currentDate.getMonth()} onDayClick={handleDayClick} monthGoals={monthGoals} onMonthGoalChange={handleMonthGoalChange} tasks={tasks} onTaskChange={handleTaskChange} appointments={appointments} yearPlanning={yearPlanning} monthAppointments={monthAppointments} onMonthAppointmentsChange={handleMonthAppointmentsChange} onYearPlanningChange={handleYearPlanningChange} onDeleteFromAll={handleDeleteFromAllViews} viewMode={viewMode} todoCalendarData={todoCalendarData} onTodoCalendarHandled={() => setTodoCalendarData(null)} onEditTodo={openTodoEditModal} todoEditRefreshKey={todoEditRefreshKey} onToggleDoneByTaskId={handleToggleDoneByTaskId} onGoToWeek={(weekNum, weekDays) => { 
                // Find the first day of the week that has a value
                if (weekDays && weekDays.length > 0) {
                  const firstDay = weekDays.find(d => d !== null);
                  if (firstDay) {
                    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), firstDay);
                    setCurrentDate(newDate);
                  }
                }
                setView('week'); 
              }} onTransferToWeek={(newAppointment) => {
                // Add transferred appointment to week view
                const updatedAppointments = [...(appointments || []), newAppointment];
                handleAppointmentChange(updatedAppointments);
              }} />
            )}
            
            {view === 'week' && (
              <WeekView currentDate={currentDate} weekGoals={weekGoals} onWeekGoalChange={handleWeekGoalChange} dayFocus={dayFocus} onDayFocusChange={handleDayFocusChange} appointments={appointments} onAppointmentChange={handleAppointmentChange} onDeleteFromAll={handleDeleteFromAllViews} googleCalendars={googleCalendars} primaryCalendar={primaryCalendar} viewMode={viewMode} todoCalendarData={todoCalendarData} onTodoCalendarHandled={() => setTodoCalendarData(null)} onEditTodo={openTodoEditModal} todoEditRefreshKey={todoEditRefreshKey} onToggleAppointmentDone={handleToggleAppointmentDone} />
            )}
            
            {view === 'todos' && (
              <TodosView appointments={appointments} onTransferToCalendar={handleTransferToCalendar} onBulkTransferToWeek={(newAppointment) => {
                const updatedAppointments = [...(appointments || []), newAppointment];
                handleAppointmentChange(updatedAppointments);
              }} onToggleAppointmentDone={handleToggleAppointmentDone} initialTab={location.state?.todosTab} />
            )}
          </>
        )}

        {/* Habit Tracker */}
        {activeButton === 'tracking' && (
          <HabitTracker onHabitsChanged={handleHabitsChanged} />
        )}

        {/* Placeholder for events */}
        {activeButton === 'events' && (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
            <p className="text-slate-500">Diese Funktion kommt bald...</p>
          </div>
        )}
      </div>

      {/* Edit Todo Modal - global, rendered at root level for all 6 views */}
      {editingTodo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-purple-500" />
                Aufgabe bearbeiten
              </h3>
              <button onClick={() => setEditingTodo(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <EditTodoForm todo={editingTodo.todo} onSave={handleSaveTodoEdit} onCancel={() => setEditingTodo(null)} />
          </div>
        </div>
      )}

      {/* Zielplanungs-Kalender Onboarding */}
      <ZielkalenderOnboarding
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onNavigateTo={setActiveButton}
      />

    </div>
  );
}
